import React, { useEffect, useState } from 'react';
import { api as apiClient } from '../../lib/api';
import type { Workspace } from '@sahaj/types';
import { Search, Building2, Filter, Copy, Calendar, ChevronLeft, ChevronRight, ArrowUpDown, Power, PowerOff } from 'lucide-react';

export const PlatformWorkspacesPage: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWorkspaces = async () => {
    try {
      const data = await apiClient.platform.getWorkspaces(0, 50);
      setWorkspaces(data.content);
    } catch (error) {
      console.error('Failed to fetch workspaces', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const toggleStatus = async (workspace: Workspace) => {
    const newStatus = workspace.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    if (!window.confirm(`Are you sure you want to change the status of ${workspace.name} to ${newStatus}?`)) {
      return;
    }
    
    try {
      await apiClient.platform.updateWorkspaceStatus(workspace.id, newStatus);
      // Optimistic update
      setWorkspaces(prev => 
        prev.map(w => w.id === workspace.id ? { ...w, status: newStatus } : w)
      );
    } catch (error) {
      console.error('Failed to update workspace status', error);
      alert('Failed to update workspace status');
    }
  };

  const totalWorkspaces = workspaces.length;
  const activeWorkspaces = workspaces.filter(w => w.status === 'ACTIVE').length;

  return (
    <div className="w-full font-sans text-foreground bg-transparent">
      <div className="w-full">
        {/* Top Bar replacing Title and Summary Card */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-card rounded-2xl mb-6">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[13px] font-bold text-muted-foreground mb-0.5">Total Workspaces</p>
                <p className="text-xl font-bold text-foreground">{totalWorkspaces}</p>
              </div>
            </div>
            <div className="h-10 w-[1px] bg-border"></div>
            <div>
              <p className="text-[13px] font-bold text-muted-foreground mb-0.5">Active Workspaces</p>
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{activeWorkspaces}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search workspaces..."
                className="pl-10 pr-4 py-2.5 border border-border rounded-xl bg-background text-sm w-72 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition shadow-xs placeholder:text-muted-foreground text-foreground"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-xl bg-card text-[13px] font-medium text-foreground hover:bg-muted transition shadow-xs">
              <Filter className="w-4 h-4" />
              Filter
              <ChevronRight className="w-4 h-4 rotate-90 text-muted-foreground ml-1" />
            </button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-muted/40 border-b border-border text-muted-foreground">
                  <tr>
                    <th className="px-6 py-4 font-bold text-[13px] cursor-pointer hover:text-foreground transition w-1/3">
                      <div className="flex items-center gap-2">
                        Workspace <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </th>
                    <th className="px-6 py-4 font-bold text-[13px] cursor-pointer hover:text-foreground transition">
                      <div className="flex items-center gap-2">
                        Slug <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </th>
                    <th className="px-6 py-4 font-bold text-[13px] cursor-pointer hover:text-foreground transition">
                      <div className="flex items-center gap-2">
                        Created <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </th>
                    <th className="px-6 py-4 font-bold text-[13px] cursor-pointer hover:text-foreground transition">
                      <div className="flex items-center gap-2">
                        Status <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </th>
                    <th className="px-6 py-4 font-bold text-[13px] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {workspaces.map(workspace => (
                    <tr key={workspace.id} className="hover:bg-muted/30 transition group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-lg shrink-0">
                            {workspace.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-foreground text-[14px]">{workspace.name}</div>
                            <div className="text-[13px] text-muted-foreground mt-0.5 font-medium">Tenant workspace</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-lg bg-muted text-foreground text-[13px] font-semibold tracking-tight border border-border/60">
                          {workspace.slug}
                          <button className="text-muted-foreground hover:text-primary transition" onClick={() => navigator.clipboard.writeText(workspace.slug)}>
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2.5 text-muted-foreground font-semibold text-[13px]">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {new Date(workspace.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wide uppercase ${
                          workspace.status === 'ACTIVE' 
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                            : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${workspace.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                          {workspace.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleStatus(workspace)}
                          className={`p-2 rounded-xl transition ${
                            workspace.status === 'ACTIVE'
                              ? 'text-rose-500 hover:bg-rose-500/10'
                              : 'text-emerald-500 hover:bg-emerald-500/10'
                          }`}
                          title={workspace.status === 'ACTIVE' ? 'Disable Workspace' : 'Activate Workspace'}
                        >
                          {workspace.status === 'ACTIVE' ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {workspaces.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground font-medium text-[13px]">
                        No workspaces found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-card">
              <div className="text-[13px] text-muted-foreground font-medium">
                Showing 1 to {totalWorkspaces} of {totalWorkspaces} workspaces
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition border border-border bg-card shadow-xs disabled:opacity-40" disabled>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-[13px]">
                  1
                </button>
                <button className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition border border-border bg-card shadow-xs disabled:opacity-40" disabled>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
